import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  Renderer2,
} from '@angular/core';

import { WorkflowFormFieldOutputRequestFileCatsPopulated } from '@features/requests/request-form/components/request-form-workflows/types';
import { TuiAccordion, TuiAvatar, TuiTooltip } from '@taiga-ui/kit';
import { TuiHint, TuiIcon, TuiScrollbar } from '@taiga-ui/core';
import { ImagePreviewComponent } from '@core/components/image-preview/image-preview.component';
import { RequestFile } from '@core/types';
import { isImageByFilename } from '@core/helpers';
import { environment } from '@environment/environment';
import { RequestModel } from '@core/models';
import { FileGroup } from '@features/requests/request-form/components/request-form-summary/types';
import { TranslatePipe } from '@ngx-translate/core';
import { FileDownloadService } from '@core/services/file-download.service';

@Component({
  selector: 'x-output-request-file-cats',
  imports: [
    TuiAccordion,
    TuiHint,
    ImagePreviewComponent,
    TuiTooltip,
    TuiIcon,
    TranslatePipe,
    TuiAvatar,
    TuiScrollbar,
  ],
  templateUrl: './output-request-file-cats.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class OutputRequestFileCatsComponent {
  field = input.required<WorkflowFormFieldOutputRequestFileCatsPopulated>();
  request = input<RequestModel | null>(null);

  private readonly fileDownloadService = inject(FileDownloadService);
  private readonly renderer = inject(Renderer2);

  // Output emitter for image preview
  showPreviewImageData = output<[RequestFile, FileGroup, number]>();

  /**
   * Check if file is an image based on filename
   */
  protected isImage(filename: RequestFile['filename']): boolean {
    return isImageByFilename(filename);
  }

  /**
   * Get file URL for preview or download
   */
  protected getFileUrl(file: RequestFile): string {
    return environment.staticUrl + file.path;
  }

  /**
   * Handle file click - show preview for images, download for other files
   */
  protected onFileClick(file: RequestFile): void {
    if (this.isImage(file.filename)) {
      // Calculate global index across all files
      const allFiles = this.getAllFiles();
      const globalIndex = allFiles.findIndex(f => f.id === file.id);

      // Show image preview with all files from all categories
      const fileGroup: FileGroup = {
        id: 0, // 0 means mixed categories
        name: this.getFileCatsNames(),
        files: allFiles,
        filesExists: allFiles.length > 0,
        multi: true,
      };

      this.showPreviewImageData.emit([file, fileGroup, globalIndex]);

      return;
    }

    if (file.filename.toLowerCase().endsWith('.pdf')) {
      this.fileDownloadService.openPdfByPath(
        file.path,
        file.filename || file.path.split('/').pop() || file.path,
      );

      return;
    }

    this.fileDownloadService.downloadFile(
      environment.downloadFilesUrl + file.path,
      file.filename || file.path.split('/').pop() || file.path,
      false,
      this.renderer,
    );
  }

  /**
   * Get all files from all categories for preview component
   */
  protected getAllFiles(): RequestFile[] {
    const fieldData = this.field();

    return fieldData.fileCats.flatMap(cat => cat.files || []);
  }

  /**
   * Get comma-separated file category names
   */
  protected getFileCatsNames(): string {
    const fieldData = this.field();

    return fieldData.fileCats.map(cat => cat.name).join(', ');
  }

  public getFileExtension(filename: string): string {
    const extension = filename.split('.').pop() || '';

    return extension.toUpperCase();
  }
}
